import React, { useState, useEffect } from "react";
import axios from "../axios";

const CATEGORIES = ["すべて", "車両情報", "運用情報", "要望", "バスの追加", "雑談"];

export default function BbsThreadList({ onSelectThread, currentUser = null }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newThread, setNewThread] = useState({ title: "", category: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState("すべて");
  const [searchQuery, setSearchQuery] = useState("");
  // スレッドを立てられるのは super_admin だけ（それ以外はボタンをグレーアウト）
  const canCreateThread = currentUser?.user_role_id === 1;

  useEffect(() => {
    fetchThreads();
  }, []);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/threads");
      setThreads(res.data);
    } catch {
      setError("スレッドの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newThread.title.trim()) return;
    try {
      setSubmitting(true);
      const res = await axios.post("/threads", newThread);
      setThreads([{ ...res.data, last_post_at: null, last_post_user: null }, ...threads]);
      setNewThread({ title: "", category: "" });
      setShowForm(false);
    } catch {
      alert("スレッドの作成に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredThreads = threads.filter((t) => {
    const matchCat = filterCategory === "すべて" || t.category === filterCategory;
    const matchSearch =
      searchQuery === "" ||
      t.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("ja-JP", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="thread-list-wrapper">
      {/* ヘッダーアクションバー */}
      <div className="thread-list-toolbar">
        <div className="thread-search-wrap">
          <span className="search-icon">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
          <input
            className="thread-search-input"
            type="text"
            placeholder="スレッドを検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className={`btn-new-thread ${showForm ? "active" : ""}`}
          onClick={() => setShowForm((v) => !v)}
          disabled={!canCreateThread}
        >
          {showForm ? "キャンセル" : "+ 新規スレッド"}
        </button>
      </div>

      {/* カテゴリフィルター */}
      <div className="category-filter">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`cat-chip ${filterCategory === cat ? "selected" : ""}`}
            onClick={() => setFilterCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* 新規スレッド作成フォーム */}
      {showForm && canCreateThread && (
        <form className="new-thread-form" onSubmit={handleCreateThread}>
          <div className="form-row">
            <input
              className="form-input"
              type="text"
              placeholder="スレッドタイトル"
              value={newThread.title}
              maxLength={255}
              onChange={(e) =>
                setNewThread({ ...newThread, title: e.target.value })
              }
              required
            />
            <select
              className="form-select"
              value={newThread.category}
              onChange={(e) =>
                setNewThread({ ...newThread, category: e.target.value })
              }
            >
              <option value="">カテゴリ未設定</option>
              {CATEGORIES.slice(1).map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <button className="btn-submit" type="submit" disabled={submitting}>
            {submitting ? "作成中..." : "スレッドを作成"}
          </button>
        </form>
      )}

      {/* スレッド一覧 */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <span>読み込み中...</span>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : filteredThreads.length === 0 ? (
        <div className="empty-state">スレッドが見つかりません</div>
      ) : (
        <ul className="thread-list">
          {filteredThreads.map((thread) => (
            <li
              key={thread.id}
              className="thread-item"
              onClick={() => onSelectThread(thread)}
            >
              <div className="thread-item-main">
                {thread.category && (
                  <span className="thread-cat-badge">{thread.category}</span>
                )}
                <span className="thread-title">{thread.title}</span>
              </div>
              <div className="thread-item-meta">
                <span className="thread-posts-count">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ marginRight: 3, verticalAlign: "middle" }}>
                    <path d="M2 2h12v9H9l-3 3V11H2V2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                  </svg>
                  {thread.posts_count ?? 0}
                </span>
                <span className="thread-date">
                  {thread.last_post_at
                    ? `${thread.last_post_user ?? "不明"} / ${formatDate(thread.last_post_at)}`
                    : formatDate(thread.created_at)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
